/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import cn from 'classnames';

import {observer} from 'mobx-react';
import {omit, get} from 'mad-utils/lib/react';

/**************************************** PROJECT MODULES *****************************************/
import {onSubmitKeyPress} from '@canimmunize/core-imports';

/********************************************* EXPORT *********************************************/
/**
 * Single select button
 *  _______________
 * |     ICON      |
 * |     HERE      |
 * |               |
 * |   text here   |
 * |_______________|
 *
 * Example uses: female, male, & relationship buttons in Create New Record modal
 *
 * @param {Object} props:
 *        @param id          {string} DOM attribute 'id' [OPTIONAL]
 *        @param style       {object} DOM attribute 'style' [OPTIONAL]
 *        @param isSelected  {boolean} If true, used 'selected' styles
 *        @param buttonLabel {string} Text or React elements to display
 *        @param onClick     {Function} Click handler
 *        @param sizingCss   {string} CSS classes to size the component [OPTIONAL]
 *                            Default: `w-175 h-60`
 *
 * @return {ReactElement} A constructed react component - single button
 */
export const CISelectButton = observer(
    (
        props: React.DOMAttributes<HTMLButtonElement> & {
            isSelected: boolean;
            buttonLabel: string | ReactChildren;
            onClick: React.EventHandler<React.MouseEvent<HTMLButtonElement>>;
            sizingCss?: string;
            ciFontSize?: number | string;
            style?: React.CSSProperties;
            id?: string;
        }
    ) => (
        <button
            id={props.id || null}
            className={cn(
                props.sizingCss || cn(`w-160 h-60`),
                `bs-solid br-4`,
                `ta-center`,
                props.isSelected
                    ? cn(`border-sky-blue bw-1`, `sky-blue bg-faint-blue`)
                    : cn(
                          `border-near-light-gray bw-0pt5`,
                          `near-light-gray`,
                          `bg-white bg-fainter-blue-hover`
                      )
            )}
            style={{
                padding: get(props, `style.padding`, 18),
                fontSize: props.ciFontSize || 15,
                ...props.style,
            }}
            onClick={props.onClick}
            onKeyUp={onSubmitKeyPress(props.onClick)}
            {...omit(props, [`isSelected`, `sizingCss`, `buttonLabel`, `ciFontSize`, `padding`])}
        >
            {props.buttonLabel}
        </button>
    )
);

CISelectButton.displayName = `CISelectButton`;
